// src/utils/socketEvents.js
//
// Socket.IO Event Names (Frontend)
// =================================
//
// PURPOSE
// -------
// This file is the frontend-side single source of truth for all Socket.IO event
// names used by the application.
//
// In this architecture, socket event names are strict string contracts between:
// - backend/listeners/socket.js
// - backend/listeners/socketEmitter.js
// - frontend socket client / dashboard / chat consumers
//
// A single typo silently breaks realtime behavior.
// Therefore:
// - no raw socket event strings in components
// - no raw socket event strings in socket client
// - backend/frontend contract changes should be committed together
//
// IMPORTANT ARCHITECTURAL CONTEXT
// -------------------------------
// Current app model:
//
// 1) HTTP is authoritative
//    - writes happen via REST
//    - dashboard state comes from GET /my-page
//    - chat messages are created via HTTP
//
// 2) Socket.IO is invalidation / notification only
//    - backend emits small payloads
//    - frontend reacts by refetching or lightly updating local view
//
// 3) Protected work-item rooms exist
//    - user room:      user:<userId>
//    - work item room: workItem:<key>
//
// 4) Object KEYS vs VALUES
//    - keys in this file are frontend-friendly constant names
//    - values are real wire-level event names and MUST match backend exactly
//
// DESIGN GOALS
// ------------
// ✅ Discoverability
// ✅ Safety
// ✅ Direction clarity
// ✅ Backward-compatible imports
// ✅ Optional grouped convenience exports

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Main event registry                                                        */
/* -------------------------------------------------------------------------- */

/**
 * IMPORTANT:
 * - keys are local constant names
 * - values are the real transport contract
 * - changing a value requires matching backend changes
 */
export const SOCKET_EVENTS = Object.freeze({
  /* ------------------------------------------------------------------------ */
  /* A) Application events (SERVER -> CLIENT)                                 */
  /* ------------------------------------------------------------------------ */

  /**
   * Backend emitted after a chat message was created via HTTP and committed.
   *
   * Typical backend target:
   * - workItem:<key>
   *
   * Typical payload:
   *   {
   *     workItemKey: "todo:123" | "leave:9223372036854775807",
   *     threadId: 1,
   *     message: {
   *       id,
   *       content,
   *       work_item_key,
   *       user_id,
   *       user,
   *       createdAt,
   *       ...
   *     }
   *   }
   *
   * Frontend usage:
   * - open chat panels append/merge the message
   * - related metadata may be refreshed if needed
   *
   * Important:
   * - this is not authoritative chat loading
   * - it is only a realtime hint for currently connected viewers
   */
  WORK_ITEM_CHAT_MESSAGE: "workItemChatMessage",

  /**
   * Backend emitted when dashboard/my-page data should be refreshed.
   *
   * Typical backend target:
   * - user:<userId>
   *
   * Typical payload:
   *   {
   *     reason: "WORK_ITEM_CHAT_MESSAGE" | "WORK_ITEM_SEEN" | "TODO_DONE" | ...,
   *     workItemKey?: "todo:123" | "leave:555",
   *     at?: "2026-03-28T20:15:00.000Z"
   *   }
   *
   * Frontend usage:
   * - Dashboard debounces and refetches GET /my-page
   *
   * Important:
   * - keep payload small
   * - dashboard truth remains HTTP-only
   */
  MY_PAGE_INVALIDATED: "myPageInvalidated",

  /**
   * Backend emits this shortly before a controlled shutdown/restart.
   *
   * This event carries no business data and must not log users out.
   * It is only an early technical hint that the current Socket.IO transport is
   * about to close, so the frontend may prepare a clean reconnect using the
   * current tab-local session identity.
   */
  SERVER_RESTARTING: "serverRestarting",

  /* ------------------------------------------------------------------------ */
  /* B) Security / diagnostics (SERVER -> CLIENT)                             */
  /* ------------------------------------------------------------------------ */

  /**
   * Backend refused a protected room join.
   *
   * Typical reasons:
   * - UNAUTHORIZED
   * - SESSION_INVALID
   * - FORBIDDEN
   * - INVALID_KEY
   *
   * Example payload:
   *   { reason: "UNAUTHORIZED" }
   *
   * Frontend usage:
   * - dev logging
   * - optional future diagnostics
   *
   * Important:
   * - this event alone should NOT cause hard logout
   * - auth decisions remain owned by apiClient/AuthContext
   */
  JOIN_DENIED: "joinDenied",

  /* ------------------------------------------------------------------------ */
  /* C) DEV helper (CLIENT -> SERVER)                                         */
  /* ------------------------------------------------------------------------ */

  /**
   * DEV-only socket diagnostics helper.
   *
   * Example:
   *   socket.emit(SOCKET_EVENTS.WHOAMI, null, (ack) => console.log(ack));
   *
   * Expected ack:
   *   {
   *     ok: true,
   *     authed: true | false,
   *     user: { id, username, role } | null,
   *     rooms: [...]
   *   }
   */
  WHOAMI: "whoami",

  /* ------------------------------------------------------------------------ */
  /* D) Optional generic rooms (CLIENT -> SERVER)                             */
  /* ------------------------------------------------------------------------ */

  /**
   * Generic room join.
   *
   * This app should mostly prefer:
   * - backend-managed user rooms
   * - protected work-item rooms
   *
   * Generic rooms are mainly for diagnostics / low-level utility use.
   */
  JOIN_ROOM: "joinRoom",

  /**
   * Generic room leave.
   */
  LEAVE_ROOM: "leaveRoom",

  /* ------------------------------------------------------------------------ */
  /* E) Protected work-item rooms (CLIENT -> SERVER)                          */
  /* ------------------------------------------------------------------------ */

  /**
   * Join a protected work-item room.
   *
   * Example:
   *   socket.emit(SOCKET_EVENTS.JOIN_WORK_ITEM, { workItemKey }, ack)
   *
   * Backend may validate:
   * - handshake auth presence
   * - active session state
   * - work item key format
   * - permission to see that work item
   */
  JOIN_WORK_ITEM: "joinWorkItem",

  /**
   * Leave a protected work-item room.
   */
  LEAVE_WORK_ITEM: "leaveWorkItem",

  /* ------------------------------------------------------------------------ */
  /* F) Socket.IO built-in lifecycle events                                   */
  /* ------------------------------------------------------------------------ */

  /**
   * Built-in Socket.IO "connected successfully" event.
   */
  CONNECT: "connect",

  /**
   * Built-in Socket.IO disconnect event.
   *
   * Example reasons:
   * - "io client disconnect"
   * - "transport close"
   * - "ping timeout"
   */
  DISCONNECT: "disconnect",

  /**
   * Built-in Socket.IO connect error event.
   */
  CONNECT_ERROR: "connect_error",
});

/* -------------------------------------------------------------------------- */
/* Optional grouped exports                                                   */
/* -------------------------------------------------------------------------- */

/**
 * These grouped exports are optional readability helpers.
 * They do not introduce any additional event names.
 */

export const SERVER_EVENTS = Object.freeze({
  WORK_ITEM_CHAT_MESSAGE: SOCKET_EVENTS.WORK_ITEM_CHAT_MESSAGE,
  MY_PAGE_INVALIDATED: SOCKET_EVENTS.MY_PAGE_INVALIDATED,
  SERVER_RESTARTING: SOCKET_EVENTS.SERVER_RESTARTING,
  JOIN_DENIED: SOCKET_EVENTS.JOIN_DENIED,
});

export const CLIENT_EVENTS = Object.freeze({
  WHOAMI: SOCKET_EVENTS.WHOAMI,
  JOIN_ROOM: SOCKET_EVENTS.JOIN_ROOM,
  LEAVE_ROOM: SOCKET_EVENTS.LEAVE_ROOM,
  JOIN_WORK_ITEM: SOCKET_EVENTS.JOIN_WORK_ITEM,
  LEAVE_WORK_ITEM: SOCKET_EVENTS.LEAVE_WORK_ITEM,
});

export const SOCKET_LIFECYCLE_EVENTS = Object.freeze({
  CONNECT: SOCKET_EVENTS.CONNECT,
  DISCONNECT: SOCKET_EVENTS.DISCONNECT,
  CONNECT_ERROR: SOCKET_EVENTS.CONNECT_ERROR,
});

/* -------------------------------------------------------------------------- */
/* Dev-only sanity checks                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Detect accidental duplicate event string values in development.
 *
 * Duplicate values are not always invalid,
 * but in practice they often signal copy/paste mistakes.
 */
function assertUniqueEventValuesDev(registry, registryName) {
  if (!DEV) return;

  const entries = Object.entries(registry || {});
  const seen = new Map();

  for (const [key, value] of entries) {
    const normalized = String(value || "").trim();

    if (!normalized) {
      // eslint-disable-next-line no-console
      console.warn(`[socketEvents] Empty socket event value detected in ${registryName}.${key}`);
      continue;
    }

    if (seen.has(normalized)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[socketEvents] Duplicate socket event value "${normalized}" detected in ${registryName}: ` +
          `${seen.get(normalized)} and ${key}`
      );
    } else {
      seen.set(normalized, key);
    }
  }
}

assertUniqueEventValuesDev(SOCKET_EVENTS, "SOCKET_EVENTS");

